import { useEffect, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router";
import { FeedbackButton } from "@/components/Feedback";
import { Header } from "@/components/Header";
import { DEV_BYPASS, hasSession, ready } from "@/lib/clerk";
import { useMe } from "@/lib/me";
import { ThemeProvider } from "@/lib/theme";
import { Admin } from "@/pages/Admin";
import { Editor } from "@/pages/Editor";
import { MyBriefs } from "@/pages/MyBriefs";
import { Refused } from "@/pages/Refused";
import { SignIn } from "@/pages/SignIn";
import { Submissions } from "@/pages/Submissions";

function Spinner() {
  return (
    <div className="grid min-h-dvh place-items-center">
      <span className="sr-only">Loading</span>
      <div className="border-border border-t-primary h-6 w-6 animate-spin rounded-full border-2" />
    </div>
  );
}

/**
 * The gate, in the pattern's three states: no session → sign in; session but
 * refused by Layer 1 → an in-app message that never signs the person out of the
 * estate; both → the app.
 */
function Shell() {
  const [clerk, setClerk] = useState<"loading" | "session" | "none">(DEV_BYPASS ? "session" : "loading");

  useEffect(() => {
    if (DEV_BYPASS) return;
    let off: (() => void) | undefined;
    void ready().then((c) => {
      setClerk(hasSession() ? "session" : "none");
      off = c?.addListener(() => setClerk(hasSession() ? "session" : "none"));
    });
    return () => off?.();
  }, []);

  // Layer 2 first: without a loaded Clerk session there is no token to attach,
  // so there is nothing to ask the API yet.
  const me = useMe(clerk === "session");

  if (clerk === "loading") return <Spinner />;
  if (clerk === "none") return <SignIn />;
  if (me.isPending) return <Spinner />;
  if (me.isError) return <Refused error={me.error} />;

  return (
    <>
      <Header me={me.data} feedbackSlot={<FeedbackButton />} />
      <Routes>
        <Route path="/" element={<MyBriefs />} />
        <Route path="/briefs/:id" element={<Editor />} />
        {me.data.isAdmin ? <Route path="/review" element={<Submissions />} /> : null}
        {me.data.isSuperadmin ? <Route path="/admin" element={<Admin />} /> : null}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

export function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <Shell />
      </BrowserRouter>
    </ThemeProvider>
  );
}
